/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureSkuInfo } from 'src/core/services/azure/azureSwaggers';
import { AzureCsAccountKind, AzureCsAccountStore, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { Region } from 'src/core/types/regionTypes';
import { FormValidators } from 'src/core/utils/formValidators';
import { azureResourceModalsMessages } from 'src/modules/azure/components/azureResourceModalsMessages';
import { AzureResourcesManagerProps } from 'src/modules/azure/components/AzureResourcesManager';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

const dropdownStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

const messages = defineMessages({
    copyProject: { id: 'core.copyProjectModal.copyProject', defaultMessage: 'Copy project' },
    modalDescription: {
        id: 'core.copyProjectModal.modalDescription',
        defaultMessage:
            'Copy your project to another resource. Only the project’s data assets, settings, and models will be copied. Deployments will not be copied to the other resource.'
    },
    overwriteDescription: {
        id: 'core.copyProjectModal.overwriteDescription',
        defaultMessage: 'Overwrite project in target resource if a project with the same name exists.'
    },
    noteBYOSDescription: {
        id: 'core.copyProjectModal.noteBYOSDescription',
        defaultMessage:
            'Note: Source and destination container should include all the labeled documents. Source and destination containers should have the same name.'
    }
});

type CopyProjectModalProps = BasicModalProps<object> & {
    serviceTarget: AzureCsAccountKind;
    resourcesValidators?: ((newSubscriptionId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    subscriptionsValidators?: ((newSubscriptionId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    strategies?: {
        sku?: (sku: AzureSkuInfo) => boolean;
        region?: (region: AzureSkuInfo) => boolean;
        resource?: (resource: AzureCsAccountStore) => boolean;
        subscription?: (subscription: AzureSubscriptionStore) => boolean;
    };
    onCopyProjectClicked: (allowOverwrite: boolean, resourceSelected: AzureCsAccountStore, selectedSubscriptionId: string) => void;
    isSupportBYOS: boolean;
};

export const CopyProjectModal = observer((props: CopyProjectModalProps) => {
    const {
        strategies,
        serviceTarget,
        onCopyProjectClicked,
        resourcesValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor],
        subscriptionsValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor],
        isSupportBYOS
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [resourcesErrorMessage, setResourcesErrorMessage] = React.useState('');
    const [isOverwriteEnabled, setIsOverwriteEnabled] = React.useState<boolean>(false);
    const [subscriptionsErrorMessage, setSubscriptionsErrorMessage] = React.useState('');

    const defaultStrategies: AzureResourcesManagerProps['strategies'] = {
        resource: () => true,
        subscription: () => true,
        sku: sku => sku.kind === serviceTarget,
        region: region => region.kind === serviceTarget
    };

    const filteringStrategies: AzureResourcesManagerProps['strategies'] = {
        sku: strategies?.sku ?? defaultStrategies.sku,
        region: strategies?.region ?? defaultStrategies.region,
        resource: strategies?.resource ?? defaultStrategies.resource,
        subscription: strategies?.subscription ?? defaultStrategies.subscription
    };

    const defaultSelectedIds = {
        selectedResourceId: '',
        selectedResourceGroupId: '',
        selectedRegion: null as Region,
        selectedSkuCode: null as AzureSku['code'],
        selectedTenantId: rootStore.azureStore.session.tenantId,
        selectedSubscriptionId: rootStore.azureStore.session.subscriptionId
    };
    const [azureResourceDto, updateAzureResourceDto] = useUpdateState(defaultSelectedIds);

    const { resources, subscriptions, resourcesTracker, subscriptionTracker } = useAzureResourcesManagement({
        skuStrategy: filteringStrategies.sku,
        regionStrategy: filteringStrategies.region,
        resourceStrategy: filteringStrategies.resource,
        subscriptionStrategy: filteringStrategies.subscription,
        subscriptionId: azureResourceDto.selectedSubscriptionId,
        configs: {
            resources: true
        }
    });
    const filteredResources = React.useMemo(() => resources.filter(item => serviceTarget === item.kind), [serviceTarget, resources]);

    const resourcesOptions = React.useMemo(
        () =>
            filteredResources.map(item => ({
                key: item.id,
                text: item.name,
                data: { secondaryText: `(${item.region}, ${item.sku.code})` }
            })),
        [filteredResources]
    );

    const onSubscriptionChange = (newSubscriptionId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = subscriptionsValidators
            .reduce((acc, v) => [...acc, v(newSubscriptionId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setSubscriptionsErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setSubscriptionsErrorMessage('');
        }
        updateAzureResourceDto({ selectedSubscriptionId: newSubscriptionId, selectedResourceId: '' });
    };

    const onResourceChange = (newResourceId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = resourcesValidators
            .reduce((acc, v) => [...acc, v(newResourceId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setResourcesErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setResourcesErrorMessage('');
        }
        updateAzureResourceDto({ selectedResourceId: newResourceId });
    };

    const handleCopyProject = () => {
        const resourceSelected = filteredResources.find(a => a.id === azureResourceDto.selectedResourceId);
        onCopyProjectClicked(isOverwriteEnabled, resourceSelected, azureResourceDto.selectedSubscriptionId);
    };

    return (
        <BasicModal
            {...props}
            valid={!!azureResourceDto.selectedResourceId && !!azureResourceDto.selectedSubscriptionId}
            onSubmit={handleCopyProject}
            doneButtonText={localize(messages.copyProject)}
            dialogContentProps={{ title: localize(messages.copyProject) }}>
            <Stack tokens={{ childrenGap: 20 }}>
                <LuisText>{localize(messages.modalDescription)}</LuisText>
                {isSupportBYOS && <LuisText variant="small">{localize(messages.noteBYOSDescription)}</LuisText>}

                <LuisDropdown
                    required
                    isLoading={subscriptionTracker.isRunning}
                    selectedKey={azureResourceDto.selectedSubscriptionId}
                    errorMessage={subscriptionsErrorMessage}
                    styles={{ root: { width: '300px' } }}
                    calloutProps={{ styles: dropdownStyles }}
                    data-automation-id="subscriptionsDropdown"
                    onChange={(_, opt) => onSubscriptionChange(opt.key as string)}
                    label={localize(azureResourceModalsMessages.subscriptionLabel)}
                    options={subscriptions.map(item => ({ key: item.id, text: item.name }))}
                    placeholder={localize(azureResourceModalsMessages.subscriptionPlaceholder)}
                />

                <LuisDropdown
                    required
                    label={localize(azureResourceModalsMessages.csLanguageAccountLabel)}
                    options={resourcesOptions}
                    styles={{ root: { width: '300px' } }}
                    ariaLabel={localize(azureResourceModalsMessages.csLanguageAccountLabel)}
                    isLoading={resourcesTracker.isRunning}
                    selectedKey={azureResourceDto.selectedResourceId}
                    placeholder={localize(azureResourceModalsMessages.csAccountPlaceholderLanguage)}
                    aria-labelledby={azureResourceDto.selectedResourceId}
                    errorMessage={resourcesErrorMessage}
                    data-automation-id="csAccountsDropdown"
                    calloutProps={{ styles: dropdownStyles }}
                    onChange={(_, item) => onResourceChange(item.key as string)}
                    disabled={Boolean(subscriptionsErrorMessage) || !Boolean(azureResourceDto.selectedSubscriptionId)}
                />

                <Checkbox
                    checked={isOverwriteEnabled}
                    styles={{ root: { marginLeft: 5, borderRadius: 4 } }}
                    onChange={(_, checked: boolean) => setIsOverwriteEnabled(checked)}
                    label={localize(messages.overwriteDescription)}
                />
            </Stack>
        </BasicModal>
    );
});
